import React from "react";
import { Image } from "react-bootstrap";
import {
  Tournament,
  TournamentParticipant,
  UserGameplayOutcome,
} from "@nestcoinco/hyper-api-gateway-api-client";
import { useTranslation } from "react-i18next";

import "./Result.scss";
import { type IOpponentSummary } from "../../../jotai/atoms/tournament";
import { GameOutcome as Outcome } from "../../../utils/constants";
import ParticipantResult from "../ParticipantResult/ParticipantResult";
import GameStreak from "../GameStreak/GameStreak";

interface ResultProps {
  userOutcome?: Outcome;
  opponentOutcome?: Outcome;
  duelOpponent: IOpponentSummary | null;
  headingText: React.ReactNode;
  tournamentDetails?: Tournament;
  userInfo?: TournamentParticipant;
  opponentInfo?: TournamentParticipant;
  hasGameFailed?: boolean;
  gameplayResults?: UserGameplayOutcome[];
}

function GameBanner({
  thumbnail,
  name,
}: {
  thumbnail?: string;
  name?: string;
}) {
  return (
    <>
      <p className="d-flex justify-content-center mb-2">
        <Image
          src={thumbnail}
          alt="game banner"
          width={34}
          className="game-thumbnail"
        />
      </p>
      <p className="game-title">{name}</p>
    </>
  );
}

function OutcomeHeading({
  outcome,
  text,
}: {
  outcome?: Outcome;
  text: React.ReactNode;
}) {
  const isWin = outcome === Outcome.WIN || outcome === Outcome.DRAW;
  const isAwaiting =
    outcome === Outcome.AWAITING_OPPONENT_SCORE ||
    outcome === Outcome.OPPONENT_CANCELLED;

  const colorMap: Partial<Record<Outcome, string>> = {
    [Outcome.AWAITING_OPPONENT_SCORE]: "white",
    [Outcome.OPPONENT_CANCELLED]: "white",
    [Outcome.LOSS]: "#E3E3E3",
    [Outcome.WAITING_TO_BE_MATCHED]: "#ffce24",
  };

  return (
    <h1
      className={`main-heading slashed-zero${isWin ? " main-heading--win" : ""}${isAwaiting ? " main-heading--awaiting" : ""}`}
      style={{ color: isWin ? undefined : colorMap[outcome!] }}
    >
      {text}
    </h1>
  );
}

function StreakSection({
  outcome,
  gameplayResults,
}: {
  outcome?: Outcome;
  gameplayResults?: UserGameplayOutcome[];
}) {
  const { t } = useTranslation("games");

  if (
    outcome === Outcome.OPPONENT_CANCELLED ||
    outcome === Outcome.AWAITING_OPPONENT_SCORE ||
    !gameplayResults?.length
  ) {
    return null;
  }

  return (
    <>
      <p className="streak-label text-uppercase">
        {t("game_results_page.streak")}
      </p>
      <GameStreak outcomes={gameplayResults} />
    </>
  );
}

function Result({
  userOutcome,
  headingText,
  opponentInfo,
  userInfo,
  duelOpponent,
  tournamentDetails,
  opponentOutcome,
  hasGameFailed,
  gameplayResults,
}: ResultProps) {
  const { t } = useTranslation("games");

  return (
    <div className="result-container">
      <div className="result">
        <GameBanner
          thumbnail={tournamentDetails?.game?.thumbnail}
          name={tournamentDetails?.game?.name}
        />
        {!hasGameFailed && (
          <>
            <OutcomeHeading outcome={userOutcome} text={headingText} />
            <StreakSection
              outcome={userOutcome}
              gameplayResults={gameplayResults}
            />
          </>
        )}
      </div>

      {!hasGameFailed && (
        <div className="result participants-row d-flex align-items-start justify-content-evenly position-relative">
          <ParticipantResult
            outcome={userOutcome}
            isLoggedInUser
            username={userInfo?.username}
            avatar={userInfo?.avatarUrl}
            score={userInfo?.score}
          />
          <p className="versus">{t("versus")}</p>
          <ParticipantResult
            outcome={opponentOutcome}
            username={opponentInfo?.username ?? duelOpponent?.username}
            avatar={opponentInfo?.avatarUrl ?? duelOpponent?.avatar}
            score={opponentInfo?.score}
          />
        </div>
      )}
    </div>
  );
}

export default Result;
